/**
 * Constants and helper functions for ReportsTab.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
// @ts-ignore - glyphMap may not have types but exists at runtime
import glyphMap from 'react-native-vector-icons/glyphmaps/MaterialIcons.json';
import { ReportCategory, ReportPlatform } from './reportCatalog';

// ─── Constants ────────────────────────────────────────────────────────────────

export const REPORT_FAVORITES_STORAGE_KEY = 'analytics-hub-report-favorites';

export const CATEGORY_FILTER_ORDER: ReportCategory[] = ['Safety', 'People', 'Accuracy', 'Reliability', 'Cost', 'Navigator'];

export const CATEGORY_COLORS: Record<ReportCategory, { background: string; text: string; icon: string }> = {
	Safety: { background: '#FFE7E1', text: '#C43A17', icon: '#C43A17' },
	People: { background: '#ECE7FF', text: '#5B48C5', icon: '#5B48C5' },
	Accuracy: { background: '#E8F1FD', text: '#0B5FC6', icon: '#0B5FC6' },
	Reliability: { background: '#DFF4FA', text: '#19759A', icon: '#19759A' },
	Cost: { background: '#E2F8E0', text: '#0D7A39', icon: '#0D7A39' },
	Navigator: { background: '#C6DCFB', text: '#01346F', icon: '#01346F' },
};

/** Dark-mode variants aligned with KPI Overview category palette. */
export const CATEGORY_COLORS_DARK: Record<ReportCategory, { background: string; text: string; icon: string }> = {
	Safety: { background: '#4A1A0F', text: '#FFB09A', icon: '#FFB09A' },
	People: { background: '#2A2260', text: '#CEC0FF', icon: '#CEC0FF' },
	Accuracy: { background: '#0D2B52', text: '#99CFFF', icon: '#99CFFF' },
	Reliability: { background: '#0B2D3D', text: '#7DE8FF', icon: '#7DE8FF' },
	Cost: { background: '#0B2D1A', text: '#8FEBA8', icon: '#8FEBA8' },
	Navigator: { background: '#0D2B52', text: '#AACCFF', icon: '#AACCFF' },
};

export const PLATFORM_STYLES: Record<ReportPlatform, { background: string; text: string }> = {
	'Power BI': { background: '#FFF3D8', text: '#8A6500' },
	Tableau: { background: '#E5F6EA', text: '#2E7D32' },
	TBD: { background: '#ECEDEF', text: '#70757D' },
	Navigator: { background: '#E8F1FD', text: '#0B5FC6' },
};

export const PLATFORM_STYLES_DARK: Record<ReportPlatform, { background: string; text: string }> = {
	'Power BI': { background: '#4A3D1A', text: '#FFD699' },
	Tableau: { background: '#1A3D2E', text: '#7DD77C' },
	TBD: { background: '#2A2B2E', text: '#9AA1AB' },
	Navigator: { background: '#0D2B52', text: '#AACCFF' },
};

export const FALLBACK_PLATFORM_STYLES = { background: '#ECEDEF', text: '#70757D' };

export const FALLBACK_PLATFORM_STYLES_DARK = { background: '#2A2B2E', text: '#9AA1AB' };

export const FALLBACK_CATEGORY_COLORS = { background: '#ECEDEF', text: '#52565D', icon: '#52565D' };

// Used when a report's platform value (e.g. from backend/Admin config) doesn't
// match one of the known ReportPlatform keys, so PLATFORM_STYLES lookups never
// return undefined and crash the card renderer.
export const FALLBACK_PLATFORM_STYLE = { background: '#ECEDEF', text: '#52565D' };
export const FALLBACK_CATEGORY_COLORS_DARK = { background: '#2A2B2E', text: '#C8CDD5', icon: '#C8CDD5' };

// ─── Storage Adapter ──────────────────────────────────────────────────────────

export const storageAdapter = {
	async getItem(key: string): Promise<string | null> {
		if (Platform.OS === 'web') {
			const webStorage = globalThis?.localStorage;
			if (!webStorage) {
				return null;
			}

			try {
				return webStorage.getItem(key);
			} catch {
				return null;
			}
		}

		if (typeof AsyncStorage?.getItem === 'function') {
			return AsyncStorage.getItem(key);
		}

		return null;
	},
	async setItem(key: string, value: string): Promise<void> {
		if (Platform.OS === 'web') {
			const webStorage = globalThis?.localStorage;
			if (!webStorage) {
				return;
			}

			try {
				webStorage.setItem(key, value);
			} catch {
				// Ignore web storage write errors.
			}
			return;
		}

		if (typeof AsyncStorage?.setItem === 'function') {
			await AsyncStorage.setItem(key, value);
		}
	},
};

// ─── Icon Helpers ─────────────────────────────────────────────────────────────

/** Validate icon name against MaterialIcons glyph map; fallback to 'assessment' if invalid */
export const getValidIconName = (iconName: string): string => {
	if (glyphMap && typeof glyphMap === 'object' && iconName in glyphMap) {
		return iconName;
	}
	// Common aliases that aren't in MaterialIcons but have equivalents
	const ICON_ALIASES: Record<string, string> = {
		monitoring: 'trending-up',
		'monitoring-sharp': 'trending-up',
		query_stats: 'query-stats',
		'query-stats': 'insights',
		'bar-chart-4-bars': 'bar-chart',
		'stacked-bar-chart': 'bar-chart',
	};
	const aliased = ICON_ALIASES[iconName];
	if (aliased && glyphMap && aliased in glyphMap) {
		return aliased;
	}
	// Try replacing underscores with hyphens and vice versa
	const withHyphens = iconName.replace(/_/g, '-');
	if (glyphMap && withHyphens in glyphMap) {
		return withHyphens;
	}
	const withUnderscores = iconName.replace(/-/g, '_');
	if (glyphMap && withUnderscores in glyphMap) {
		return withUnderscores;
	}
	return 'assessment';
};

// ─── Platform Inference ───────────────────────────────────────────────────────

export const inferPlatformFromLink = (link: string): ReportPlatform => {
	const normalized = link.trim().toLowerCase();
	if (!normalized || normalized === 'tbd') {
		return 'TBD';
	}
	// Screens registered under this path (see App.tsx's internal route table) must open
	// in-app even if the backend row's reportType wasn't tagged INTERNAL_REDIRECT.
	if (normalized.includes('/navigator-reports/')) {
		return 'Navigator';
	}
	if (normalized.includes('powerbi')) {
		return 'Power BI';
	}
	if (normalized.includes('tableau')) {
		return 'Tableau';
	}
	// Relative in-app routes (e.g. "/navigator-reports/order-rules") are internal
	// Navigator screens rather than external report links. Some backend rows are
	// still tagged reportType "EXTERNAL" despite pointing at an internal route, so
	// fall back to path-shape detection to avoid surfacing a false "TBD" state.
	if (normalized.startsWith('/') && !normalized.startsWith('//')) {
		return 'Navigator';
	}
	return 'TBD';
};
